import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import AppWrapper from './AppWrapper';
import { TenantProvider } from './contexts/TenantContext';
import { PrimeReactProvider } from 'primereact/api';
import { createQueryClient } from './lib/queryClient';

// PrimeReact CSS imports
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

// Font fixes for CORS issues
// TODO: Create these files if needed
// import './styles/fonts.css';
// import './styles/font-override.css';

// Diamond theme overrides
// import './styles/themes/diamond.css';
import './styles/layout/buttons.scss';
import './styles/layout/auth.scss';

// Your custom CSS
// import './index.css';
import reportWebVitals from './reportWebVitals';

// Silence the benign "ResizeObserver loop" warning. It is NOT a real error —
// the browser simply deferred some resize callbacks to the next animation
// frame. PrimeReact's scrollable/frozen DataTable (and other resize-aware
// widgets) trigger it, and CRA's dev overlay otherwise renders it as a fatal
// red screen. Harmless in production; this only tames the dev experience.
const RESIZE_OBSERVER_MESSAGES = [
    'ResizeObserver loop completed with undelivered notifications.',
    'ResizeObserver loop limit exceeded',
];
window.addEventListener(
    'error',
    (event) => {
        if (event.message && RESIZE_OBSERVER_MESSAGES.some((m) => event.message.includes(m))) {
            event.stopImmediatePropagation();
            event.preventDefault();
            // Dismiss the webpack-dev-server error overlay if it already showed.
            const overlay = document.getElementById('webpack-dev-server-client-overlay');
            if (overlay) overlay.style.display = 'none';
        }
    },
    true, // capture — run before the dev overlay's own handler
);

// Auto-reload once when a lazy chunk fails to load (stale deployment cache)
window.addEventListener('error', (event) => {
    if (
        event.message &&
        (event.message.includes('Loading chunk') ||
            event.message.includes('ChunkLoadError') ||
            event.message.includes('Loading CSS chunk'))
    ) {
        const reloadKey = 'chunkLoadErrorReloaded';
        if (!sessionStorage.getItem(reloadKey)) {
            sessionStorage.setItem(reloadKey, '1');
            window.location.reload();
        }
    }
});
window.addEventListener('unhandledrejection', (event) => {
    const msg = event.reason?.message || event.reason || '';
    if (
        String(msg).includes('Loading chunk') ||
        String(msg).includes('ChunkLoadError') ||
        String(msg).includes('Loading CSS chunk')
    ) {
        const reloadKey = 'chunkLoadErrorReloaded';
        if (!sessionStorage.getItem(reloadKey)) {
            sessionStorage.setItem(reloadKey, '1');
            window.location.reload();
        }
    }
});

// PrimeReact configuration.
// zIndex bumps the per-category baselines so portaled overlays (Dropdown,
// Calendar, OverlayPanel, etc.) render ABOVE Sidebar/Dialog masks. Defaults
// are modal=1100, overlay=1000 — meaning a Dropdown opened inside a Sidebar
// gets z-index ~1001 and renders BEHIND the sidebar mask (1100), making the
// panel invisible. Raising overlay/menu/tooltip above modal fixes this for
// every Dropdown/Calendar in the app.
const primeReactConfig = {
    ripple: true,
    inputStyle: 'outlined',
    hideOverlaysOnDocumentScrolling: false,
    zIndex: {
        modal: 1100,
        overlay: 1200,
        menu: 1300,
        tooltip: 1400,
        toast: 1500,
    },
};

// One client for the app's lifetime. Created outside render so a re-render can
// never swap it and throw the whole cache away.
const queryClient = createQueryClient();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <QueryClientProvider client={queryClient}>
        <PrimeReactProvider value={primeReactConfig}>
            <TenantProvider>
                <AppWrapper />
            </TenantProvider>
        </PrimeReactProvider>
    </QueryClientProvider>
);

reportWebVitals();
